import { createSignal, onMount, onCleanup, Show, For } from "solid-js";

type UploadItem = {
	file: File,
	path: string,
	progress: number,
	status: string
};

export const UploadManager = ( props: any ) => {
	const [isDragging, setIsDragging] = createSignal( false );
	const [uploads, setUploads] = createSignal<UploadItem[]>( [] );

	let dragCounter = 0;

	/*
	 * ---------------------------
	 * DRAG DETECTION
	 * ---------------------------
	 */

	const handleDragEnter = e => {
		e.preventDefault();
		dragCounter++;
		setIsDragging( true );
	};

	const handleDragLeave = e => {
		e.preventDefault();
		dragCounter--;
		if ( dragCounter <= 0 ) {
			dragCounter = 0;
			setIsDragging( false );
		}
	};

	const handleDragOver = e => {
		e.preventDefault(); // REQUIRED
	};

	/*
	 * ---------------------------
	 * DROP HANDLING (FILES + FOLDERS)
	 * ---------------------------
	 */

	const handleDrop = async e => {
		e.preventDefault();

		const items = Array.from( e.dataTransfer.items || [] );
		const files = await getFilesFromItems( items );

		addFiles( files );

		dragCounter = 0;
		setIsDragging( false );
	};

	const getFilesFromItems = async ( items ): Promise<File[]> => {
		const files: any[] = [];

		const traverse = entry =>
			new Promise<void>( resolve => {
				if ( entry.isFile ) {
					entry.file( file => {
						file.fullPath = entry.fullPath;
						files.push( file );
						resolve();
					} );
				} else if ( entry.isDirectory ) {
					const reader = entry.createReader();
					reader.readEntries( async entries => {
						await Promise.all( entries.map( traverse ) );
						resolve();
					} );
				} else {
					resolve();
				}
			} );

		await Promise.all(
			items.map( item => {
				const entry = item.webkitGetAsEntry?.();
				return entry ? traverse( entry ) : Promise.resolve();
			} )
		);

		return files;
	};

	/*
	 * ---------------------------
	 * UPLOAD ENGINE
	 * ---------------------------
	 */

	const getPresignedUrl = async file => {
		const res = await fetch( "/api/upload-url", {
			method: "POST",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify( {
				filename: props.path + ( file.fullPath || file.name ).replace( /^\//, "" ),
				type: file.type
			} )
		} );

		if ( !res.ok ) {
			throw new Error( "Failed to get upload URL" );
		}

		const { url } = await res.json();
		return url;
	};

	const uploadFile = async ( file, index ) => {
		const url = await getPresignedUrl( file );

		if ( url === null ) {
			console.warn( "Received null URL, skipping upload for:", file.fullPath || file.name );
			updateUpload( index, { progress: 100, status: "skipped" } );
			return;
		}

		return new Promise<void>( ( resolve, reject ) => {
			const xhr = new XMLHttpRequest();
			xhr.open( "PUT", url );

			xhr.upload.onprogress = e => {
				if ( e.lengthComputable ) {
					const percent = Math.ceil( ( e.loaded / e.total ) * 100 );
					updateUpload( index, {
						progress: percent,
						status: "uploading"
					} );
				}
			};

			xhr.onload = () => {
				if ( xhr.status >= 200 && xhr.status < 300 ) {
					updateUpload( index, { progress: 100, status: "done" } );
					resolve();
				} else {
					updateUpload( index, { status: "error" } );
					reject();
				}
			};

			xhr.onerror = () => {
				updateUpload( index, { status: "error" } );
				reject();
			};

			xhr.send( file );
		} );
	};

	const uploadQueue = async ( startIndex, files, concurrency = 3 ) => {
		let i = 0;

		const workers = Array.from( { length: concurrency }, async () => {
			while ( i < files.length ) {
				const current = i++;
				await uploadFile( files[current], startIndex + current );
			}
		} );

		await Promise.all( workers );

		props.onComplete?.();

		setTimeout( () => {
			setUploads( prev => prev.slice( 0, -files.length ) );
		}, 5000 );
	};

	/*
	 * ---------------------------
	 * STATE HELPERS
	 * ---------------------------
	 */

	const updateUpload = ( index, update ) => {
		setUploads( prev => {
			const copy = [...prev];
			copy[index] = { ...copy[index], ...update };
			return copy;
		} );
	};

	const addFiles = files => {
		const newItems = files.map( file => ( {
			file,
			path: file.fullPath || file.name,
			progress: 0,
			status: "queued"
		} ) );

		const startIndex = uploads().length;

		setUploads( prev => [...prev, ...newItems] );

		uploadQueue( startIndex, files );
	};

	/*
	 * ---------------------------
	 * LIFECYCLE
	 * ---------------------------
	 */

	onMount( () => {
		window.addEventListener( "dragenter", handleDragEnter );
		window.addEventListener( "dragleave", handleDragLeave );
		window.addEventListener( "dragover", handleDragOver );
		window.addEventListener( "drop", handleDrop );
	} );

	onCleanup( () => {
		window.removeEventListener( "dragenter", handleDragEnter );
		window.removeEventListener( "dragleave", handleDragLeave );
		window.removeEventListener( "dragover", handleDragOver );
		window.removeEventListener( "drop", handleDrop );
	} );


	let fileInput;

	// New handler for clicking/selecting files
	const handleFileChange = ( e ) => {
		const files = Array.from( e.target.files || [] );
		if ( files.length > 0 ) {
			addFiles( files );
		}
		// Reset input so the same file can be picked twice if needed
		e.target.value = "";
	};

	// This function can be called to open the dialog
	const triggerPicker = () => fileInput?.click();

	// Expose the trigger to the parent via a prop
	onMount( () => {
		if ( props.ref ) {
			props.ref( { triggerPicker } );
		}
	} );

	/*
	 * ---------------------------
	 * UI
	 * ---------------------------
	 */

	return (
		<>
			<input
				title="Upload Files"
				type="file"
				multiple
				ref={fileInput}
				onChange={handleFileChange}
				class="hidden"
			/>

			<Show when={isDragging()}>
				<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur">
					<div class="border-2 border-dashed border-white p-12 rounded-2xl text-white text-xl">
						Drop files to upload
					</div>
				</div>
			</Show>

			<Show when={uploads().length > 0}>
				<div class="fixed z-10 bottom-4 right-4 w-96 bg-white shadow-xl rounded-xl p-4 space-y-3 max-h-96 overflow-auto">
					<For each={uploads()}>
						{item => (
							<div class="text-sm">
								<div class="flex justify-between">
									<span class="truncate">{item.path}</span>
									<span>{item?.progress ?? "-" }%</span>
								</div>

								<div class="h-2 bg-gray-200 rounded mt-1 overflow-hidden">
									<div
										class="h-full bg-blue-500 transition-all"
										style={{ width: `${item?.progress ?? 0}%` }}
									/>
								</div>

								<div class="text-xs text-gray-500 mt-1">
									{item?.status ?? "n/a"}
								</div>
							</div>
						)}
					</For>
				</div>
			</Show>
		</>
	);
};

